import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Analytics.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun'];
const REVENUE = [180000,210000,195000,240000,258000,240000];
const LEAD_SOURCES = [
  {label:'Meta Ads',value:38,color:'#F5A623'},{label:'BNI Referral',value:28,color:'#3B82F6'},
  {label:'Website',value:20,color:'#22C55E'},{label:'Direct',value:10,color:'#8B5CF6'},
  {label:'Other',value:4,color:'#EF4444'},
];
const PIPELINE = [
  {label:'New',value:12,color:'#22C55E'},{label:'Contacted',value:9,color:'#3B82F6'},
  {label:'Warm',value:10,color:'#60A5FA'},{label:'Hot',value:7,color:'#F5A623'},
  {label:'Closed',value:6,color:'#22C55E'},{label:'Lost',value:2,color:'#EF4444'},
];
const TOP_CLIENTS = [
  {name:'Elaam Bazaar',value:610000,city:'Kattapana'},{name:'Auxin Shipping',value:250000,city:'Mumbai'},
  {name:'Rao Infra',value:120000,city:'Kochi'},{name:'Singh Logistics',value:88000,city:'Delhi'},
  {name:'Sneha Corp',value:55000,city:'Mumbai'},
];

/* Client locations for dot map -- [lat, lng, city, clients] */
const CLIENT_LOCATIONS = [
  {city:'Kochi',clients:3,lat:9.93,lng:76.27},{city:'Chennai',clients:2,lat:13.08,lng:80.27},
  {city:'Delhi',clients:2,lat:28.61,lng:77.21},{city:'Mumbai',clients:2,lat:19.08,lng:72.88},
  {city:'Kozhikode',clients:1,lat:11.26,lng:75.78},{city:'Bangalore',clients:1,lat:12.97,lng:77.59},
  {city:'Hyderabad',clients:1,lat:17.39,lng:78.49},{city:'Dubai',clients:1,lat:25.20,lng:55.27},
  {city:'Singapore',clients:1,lat:1.35,lng:103.82},
];

const fmtINR = v => v>=100000?'₹'+(v/100000).toFixed(1)+'L':'₹'+(v/1000).toFixed(0)+'K';

/* ── Dot World Map ── */
function DotWorldMap() {
  const W = 580, H = 260;
  /* Equirectangular projection -- full world */
  const toXY = (lat, lng) => ({
    x: ((lng + 180) / 360) * W,
    y: ((90 - lat) / 180) * H,
  });

  /* Land mass dot grids */
  const landDots = [];

  const addGrid = (latMin, latMax, lngMin, lngMax, step, check) => {
    for (let lat = latMin; lat <= latMax; lat += step) {
      for (let lng = lngMin; lng <= lngMax; lng += step) {
        if (!check || check(lat, lng)) landDots.push({lat, lng});
      }
    }
  };

  /* India */
  addGrid(8, 37, 68, 97, 1.4, (la, ln) =>
    (la>8&&la<15&&ln>76&&ln<80) ||
    (la>8&&la<23&&ln>68&&ln<80) ||
    (la>15&&la<23&&ln>72&&ln<92) ||
    (la>23&&la<30&&ln>70&&ln<88) ||
    (la>30&&la<37&&ln>73&&ln<80)
  );
  /* Europe */
  addGrid(36, 70, -10, 40, 2, (la, ln) =>
    (la>36&&la<48&&ln>-5&&ln<28) ||
    (la>48&&la<58&&ln>-5&&ln<22) ||
    (la>58&&la<70&&ln>5&&ln<30) ||
    (la>44&&la<52&&ln>14&&ln<40)
  );
  /* Africa */
  addGrid(-35, 37, -18, 52, 2, (la, ln) =>
    (la>-35&&la<-20&&ln>16&&ln<36) ||
    (la>-20&&la<5&&ln>10&&ln<42) ||
    (la>5&&la<15&&ln>-15&&ln<42) ||
    (la>15&&la<37&&ln>-5&&ln<36)
  );
  /* North America */
  addGrid(15, 72, -168, -52, 2, (la, ln) =>
    (la>15&&la<30&&ln>-118&&ln<-86) ||
    (la>30&&la<50&&ln>-125&&ln<-70) ||
    (la>50&&la<72&&ln>-140&&ln<-60)
  );
  /* South America */
  addGrid(-56, 12, -82, -34, 2, (la, ln) =>
    (la>-10&&la<12&&ln>-82&&ln<-46) ||
    (la>-30&&la<-10&&ln>-75&&ln<-34) ||
    (la>-56&&la<-30&&ln>-76&&ln<-48)
  );
  /* Asia (ex India) */
  addGrid(0, 55, 40, 145, 2, (la, ln) =>
    (la>35&&la<55&&ln>40&&ln<135) ||
    (la>20&&la<35&&ln>45&&ln<122) ||
    (la>0&&la<20&&ln>95&&ln<122) ||
    (la>20&&la<40&&ln>100&&ln<122)
  );
  /* Australia */
  addGrid(-44, -10, 113, 154, 2, (la, ln) =>
    (la>-44&&la<-10&&ln>113&&ln<154)
  );
  /* Arabian Peninsula */
  addGrid(12, 30, 36, 60, 1.8, (la, ln) =>
    (la>12&&la<26&&ln>36&&ln<60)
  );
  /* SE Asia */
  addGrid(0, 22, 95, 125, 1.8);
  /* Pakistan/Afghanistan */
  addGrid(24, 38, 60, 74, 1.6);
  /* Japan/Korea */
  addGrid(30, 46, 128, 146, 1.8);

  return (
    <div className="an-map-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="an-map-svg" preserveAspectRatio="xMidYMid meet">
        {/* Land dots */}
        {landDots.map((d,i) => {
          const {x,y} = toXY(d.lat, d.lng);
          return <circle key={i} cx={x} cy={y} r="1.1" fill="currentColor" opacity="0.15"/>;
        })}
        {/* Client dots */}
        {CLIENT_LOCATIONS.map((loc,i) => {
          const {x,y} = toXY(loc.lat, loc.lng);
          const r = 3 + loc.clients * 1.2;
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={r*3} fill="#F5A623" opacity="0.08"/>
              <circle cx={x} cy={y} r={r*1.8} fill="#F5A623" opacity="0.15"/>
              <circle cx={x} cy={y} r={r} fill="#F5A623" opacity="1"/>
              <circle cx={x} cy={y} r={r*0.4} fill="#fff" opacity="0.9"/>
              <text x={x} y={y-r-3} textAnchor="middle" fontSize="7" fill="currentColor" opacity="0.6">{loc.city}</text>
            </g>
          );
        })}
      </svg>
      <div className="an-map-legend">
        {CLIENT_LOCATIONS.map(loc=>(
          <div key={loc.city} className="an-map-leg-item">
            <div className="an-map-leg-dot"/>
            <span className="an-map-leg-city">{loc.city}</span>
            <span className="an-map-leg-count">{loc.clients}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Revenue Chart ── */
function RevenueBarChart() {
  const thisMonth = [180000,210000,195000,240000,258000,240000];
  const lastMonth = [155000,190000,180000,220000,240000,220000];
  const max = Math.max(...thisMonth,...lastMonth);
  const W=500,H=130,PB=20,PT=8,PL=8,PR=8;
  const cH=H-PB-PT;
  const groups=MONTHS.length;
  const groupW=(W-PL-PR)/groups;
  const barW=groupW*0.35;
  const gap=groupW*0.04;

  return (
    <div className="an-widget">
      <div className="an-widget-hd">
        <span className="an-widget-title">Revenue — this month vs last</span>
        <div style={{display:'flex',gap:12,alignItems:'center',fontSize:10}}>
          <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:8,height:8,borderRadius:2,background:'#F5A623',display:'inline-block'}}/> This month</span>
          <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:8,height:8,borderRadius:2,background:'rgba(245,166,35,0.3)',display:'inline-block'}}/> Last month</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="an-svg">
        {[0,1,2,3].map(i=>{
          const y=PT+i*(cH/3);
          return <line key={i} x1={PL} y1={y} x2={W-PR} y2={y} stroke="currentColor" strokeOpacity="0.06" strokeWidth="0.5"/>;
        })}
        {MONTHS.map((m,i)=>{
          const gx=PL+i*groupW;
          const h1=Math.max(2,(lastMonth[i]/max)*cH);
          const h2=Math.max(2,(thisMonth[i]/max)*cH);
          const y1=PT+cH-h1;
          const y2=PT+cH-h2;
          const cx=gx+groupW/2;
          return (
            <g key={m}>
              <rect x={cx-barW-gap} y={y1} width={barW} height={h1} rx="2" fill="#F5A623" opacity="0.3"/>
              <rect x={cx+gap} y={y2} width={barW} height={h2} rx="2" fill="#F5A623"/>
              <text x={cx} y={H-4} textAnchor="middle" fontSize="8.5" fill="currentColor" opacity="0.35">{m}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Pie Chart ── */
function PieChart() {
  const total = LEAD_SOURCES.reduce((s,d)=>s+d.value,0);
  let angle=-90;
  const R=58,CX=80,CY=72;
  const slices = LEAD_SOURCES.map(d=>{
    const sweep=(d.value/total)*360;
    const sA=angle*Math.PI/180, eA=(angle+sweep)*Math.PI/180;
    const laf=sweep>180?1:0;
    const path=`M${CX},${CY} L${CX+R*Math.cos(sA)},${CY+R*Math.sin(sA)} A${R},${R} 0 ${laf},1 ${CX+R*Math.cos(eA)},${CY+R*Math.sin(eA)} Z`;
    angle+=sweep; return {...d,path};
  });
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Lead sources</span></div>
      <div className="an-pie-wrap">
        <svg viewBox="0 0 160 145" className="an-pie-svg">
          {slices.map(s=><path key={s.label} d={s.path} fill={s.color} opacity="0.88"/>)}
          <circle cx={CX} cy={CY} r="26" className="an-pie-hole"/>
          <text x={CX} y={CY+1} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="currentColor" opacity="0.5">Leads</text>
        </svg>
        <div className="an-pie-legend">{LEAD_SOURCES.map(s=>(
          <div key={s.label} className="an-pie-leg-item">
            <div className="an-pie-leg-dot" style={{background:s.color}}/>
            <span className="an-pie-leg-lbl">{s.label}</span>
            <span className="an-pie-leg-val">{s.value}%</span>
          </div>
        ))}</div>
      </div>
    </div>
  );
}

/* ── Pipeline ── */
function PipelineChart() {
  const max = Math.max(...PIPELINE.map(p=>p.value));
  return (
    <div className="an-widget an-widget-full">
      <div className="an-widget-hd"><span className="an-widget-title">Lead pipeline</span><span className="an-widget-tag">By status</span></div>
      <div className="an-pipeline-grid">
        {PIPELINE.map(p=>(
          <div key={p.label} className="an-pipe-item">
            <div className="an-pipe-top">
              <span className="an-pipe-lbl">{p.label}</span>
              <span className="an-pipe-val" style={{color:p.color}}>{p.value}</span>
            </div>
            <div className="an-bar-track"><div className="an-bar-fill" style={{width:`${(p.value/max)*100}%`,background:p.color}}/></div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Top Clients ── */
function TopClients() {
  const max=Math.max(...TOP_CLIENTS.map(c=>c.value));
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Top clients</span></div>
      <div className="an-clients-list">{TOP_CLIENTS.map((c,i)=>(
        <div key={c.name} className="an-client-item">
          <div className="an-client-rank">#{i+1}</div>
          <div className="an-client-info"><div className="an-client-name">{c.name}</div><div className="an-client-city">{c.city}</div></div>
          <div className="an-client-bar-wrap"><div className="an-client-bar" style={{width:`${(c.value/max)*100}%`}}/></div>
          <div className="an-client-val">{fmtINR(c.value)}</div>
        </div>
      ))}</div>
    </div>
  );
}

/* ── Main ── */
export default function Analytics() {
  const { theme } = useTheme();

  return (
    <div className="an-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="an-main">
        <Topbar title="Analytics" subtitle="Business overview"/>

        {/* KPI row */}
        <div className="an-kpi-row">
          {[
            {label:'Revenue this month',val:'₹2.58L',chg:'+8% vs May',color:'#F5A623'},
            {label:'Active leads',val:'48',chg:'+6 new',color:'#22C55E'},
            {label:'Tasks done',val:'94%',chg:'2 overdue',color:'#3B82F6'},
            {label:'Team present',val:'9/12',chg:'3 on leave',color:'#8B5CF6'},
          ].map(s=>(
            <div key={s.label} className="an-kpi-card">
              <div className="an-kpi-lbl">{s.label}</div>
              <div className="an-kpi-val" style={{color:s.color}}>{s.val}</div>
              <div className="an-kpi-chg">{s.chg}</div>
            </div>
          ))}
        </div>

        <div className="an-content">
          {/* Row 1: Revenue + Pie */}
          <div className="an-row-2">
            <RevenueBarChart/>
            <PieChart/>
          </div>

          {/* Row 2: Pipeline full width */}
          <PipelineChart/>

          {/* Row 3: Top clients + Client map */}
          <div className="an-row-2">
            <TopClients/>
            <div className="an-widget">
              <div className="an-widget-hd">
                <span className="an-widget-title">Client locations</span>
                <span className="an-widget-tag">{CLIENT_LOCATIONS.reduce((s,c)=>s+c.clients,0)} clients · {CLIENT_LOCATIONS.length} cities</span>
              </div>
              <DotWorldMap/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
